import React, { useState } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import {
  getSubscribeEmailList,
  updateSubscribeEmailStatus,
  Delete
} from "adminApi/SubscribeEmailListApi/SubscribeEmailList";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../json/SubscribeEmaillistFeilds.json";
import Switch from "Common/Custom/Switch";
import toast from "react-hot-toast";

/**
 * SubscribeEmaillistTable Component
 * Displays a paginated table of subscribers with status toggle functionality.
 */
const SubscribeEmaillistTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);
  const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);

  // Verify if user has permissions to edit statuses
  const canUpdate = useRoleVerify("", ["can_edit"]);
  const sizeOptions = [10, 20, 50, 100];
  const { tabledata } = fields;

  // Fetch Hook for paginated subscriber data
  const { data, meta, loading, error, refetch } = usePaginatedFetch(getSubscribeEmailList, {
    page,
    limit,
    search: debouncedSearch,
    title: "Member List",
  });

  /**
   * Handles toggling of subscriber status (Active/Inactive)
   * @param {Object} item - The subscriber record
   */
  const handleStatusToggle = async (item) => {
  if (!canUpdate) {
    toast.error("Missing Update Permission");
    return;
  }

  const newStatus = item.status === "1" ? "0" : "1";

  try {
    const response = await updateSubscribeEmailStatus({
      data: {
        emailid: item.emailid,
        status: newStatus,
      },
    });

    if (response?.success) {
      toast.success(
        `Member status updated to ${
          newStatus === "1" ? "Active" : "Inactive"
        }`
      );
      refetch();
    } else {
      toast.error(response?.error || "Failed to update status");
    }
  } catch (error) {
    console.error(error);
    toast.error("An error occurred while updating status");
  }
};

  // Auto-generate display fields from JSON configuration
  const displayFields = generateDisplayFields(tabledata);

  // Transform data to include an interactive Switch for the status column
  const processedData = data.map((item) => ({
    ...item,
    status: (
      <Switch
        field={
          {
            name: `status_${item.emailid}`,
            disabled: !canUpdate
          }}
        values={item.status}
        handleChange={() => handleStatusToggle(item)}
        ValueOne="1"
        ValueTwo="0"
        LabelOne="Active"
        LabelTwo="Inactive"
      />
    ),
    created_at: item.created_at ? new Date(item.created_at).toLocaleDateString() : "N/A"
  }));

  // Table Configuration Hook
  const { formattedData, columns } = useTableConfig({
    data: processedData,
    page,
    limit,
    canShowAction: canShowAction, 
    refetch,
    config: {
      entityName: "Member",
      editUrl: "member-email-edit",
      idKey: "emailid",
      deleteApi: Delete,
      displayFields,
    },
  });

  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };

  const onPageChange = (p) => setPage(p);

  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <DataTable
      columns={columns}
      data={formattedData}
      options={{
        loading,
        error,
        sortableColumns: displayFields.map((f) => f.accessor),
        copy: true,
        csv: true,
        pdf: true,
        print: true,
        fileName: "Subscriber_List",
      }}
      Search={{
        search: true,
        SearchInputValue: search,
        handleSearch: handleSearch,
      }}
      Entries={{
        Entries: true,
        limit,
        onLimitChange,
        options: sizeOptions,
      }}
      pagination={{
        isActive: true,
        meta,
        onPageChange,
      }}
    />
  );
};

export default SubscribeEmaillistTable;
